// Lucky number reading — a deterministic, Thai-numerology-flavored "astrology
// reading" for a phone number. Pure function of the digits: no network, no
// randomness, so the same number always reads the same. Entertainment only —
// the page says so.
//
// The system (loosely modeled on Thai "ber mongkol" practice):
// - Each digit carries a planetary identity (1 Sun … 9 Ketu, 0 the void).
// - Adjacent digit PAIRS are scored; the tail of the number (the digits you
//   say most often) weighs heaviest.
// - The digit sum reduces to a 1–9 "path number" for the overall reading.

const DIGITS = {
  0: { planet: 'the Void', trait: 'openness', keywords: ['potential', 'mystery', 'blank pages'] },
  1: { planet: 'the Sun', trait: 'leadership', keywords: ['authority', 'confidence', 'independence'] },
  2: { planet: 'the Moon', trait: 'charm', keywords: ['sensitivity', 'diplomacy', 'popularity'] },
  3: { planet: 'Mars', trait: 'drive', keywords: ['courage', 'energy', 'bluntness'] },
  4: { planet: 'Mercury', trait: 'communication', keywords: ['wit', 'negotiation', 'adaptability'] },
  5: { planet: 'Jupiter', trait: 'wisdom', keywords: ['fairness', 'respect', 'good judgment'] },
  6: { planet: 'Venus', trait: 'grace', keywords: ['beauty', 'art', 'money luck'] },
  7: { planet: 'Saturn', trait: 'endurance', keywords: ['patience', 'discipline', 'hard lessons'] },
  8: { planet: 'Rahu', trait: 'ambition', keywords: ['risk', 'intensity', 'sudden change'] },
  9: { planet: 'Ketu', trait: 'fortune', keywords: ['progress', 'protection', 'spiritual luck'] },
};

// Thai astrology ties each planet to a day and color.
const PLANET_DAY = {
  1: { day: 'Sunday', color: 'red' },
  2: { day: 'Monday', color: 'yellow' },
  3: { day: 'Tuesday', color: 'pink' },
  4: { day: 'Wednesday', color: 'green' },
  5: { day: 'Thursday', color: 'orange' },
  6: { day: 'Friday', color: 'light blue' },
  7: { day: 'Saturday', color: 'purple' },
  8: { day: 'Wednesday night', color: 'grey' },
  9: { day: 'no fixed day', color: 'gold' },
  0: { day: 'no fixed day', color: 'white' },
};

// Notable pairs, scored 0–10. Order-insensitive entries are listed once and
// mirrored below; a few pairs read differently by direction and are listed
// explicitly. Everything else falls back to a blend of its two digits.
const PAIR_READINGS = {
  '15': [9, 'The mentor pair — wisdom meets authority; elders and bosses take your side'],
  '24': [9, 'The silver tongue — charm plus wit; beloved by customers and crowds'],
  '36': [8, 'Bold creativity — energy channelled into art and enterprise'],
  '45': [10, 'The scholar’s pair — famously auspicious for speech, study, and law'],
  '46': [9, 'Words that make money — persuasion pays'],
  '56': [9, 'Wisdom and grace — a favorite for reputation and refinement'],
  '59': [10, 'The blessed pair — good judgment under lucky stars'],
  '19': [8, 'Sunrise fortune — leadership carried forward by momentum'],
  '26': [8, 'Charm and beauty — magnetic in society, lucky in love'],
  '28': [7, 'Popular ambition — bold moves with public support'],
  '29': [8, 'The gentle star — kindness that returns as luck'],
  '14': [8, 'The executive pair — decisive and articulate'],
  '16': [7, 'Sunlit grace — respected and well-liked'],
  '39': [7, 'The campaigner — energy that finishes what it starts'],
  '42': [9, 'Sweet-talking success — negotiation is your superpower'],
  '44': [8, 'Double Mercury — a born communicator'],
  '55': [8, 'Double Jupiter — deep judgment, natural teacher'],
  '66': [8, 'Double Venus — surrounded by beauty and comfort'],
  '99': [8, 'Double Ketu — strong spiritual protection'],
  '69': [8, 'Art and fortune — creative work attracts money'],
  '65': [8, 'Graceful wisdom — taste and judgment in balance'],
  '95': [9, 'Fortune guided by wisdom — rarely a wrong step'],
  '96': [8, 'Lucky in love and money alike'],
  '89': [7, 'High risk, high reward — ambition under a lucky star'],
  '11': [6, 'Double Sun — proud and independent, occasionally stubborn'],
  '22': [6, 'Double Moon — deeply intuitive, sometimes moody'],
  '78': [5, 'The iron pair — heavy burdens build real power'],
  '33': [5, 'Double Mars — fearless, but pick your battles'],
  '88': [5, 'Double Rahu — a gambler’s number; fortunes swing'],
  '13': [2, 'Sun against Mars — friction, arguments, burned bridges'],
  '31': [2, 'Mars against Sun — a short fuse around authority'],
  '27': [2, 'Moon under Saturn — worry and sleepless nights'],
  '72': [2, 'Saturn over Moon — delays in matters of the heart'],
  '48': [3, 'Mercury with Rahu — clever words that find trouble'],
  '84': [3, 'Rahu with Mercury — rumors travel fast; verify twice'],
  '38': [3, 'Mars with Rahu — accident-prone energy; drive carefully'],
  '83': [3, 'Rahu with Mars — conflict finds you; walk away winning'],
  '37': [3, 'Mars ground down by Saturn — effort meets obstacles'],
  '73': [3, 'Saturn against Mars — slow grinding struggles'],
  '17': [4, 'Sun in Saturn’s shadow — success arrives late but solid'],
  '71': [4, 'Saturn before the Sun — patience before recognition'],
  '00': [3, 'The double void — drifting; set a destination'],
  '07': [3, 'Void and Saturn — lonely stretches; keep good company'],
  '70': [3, 'Saturn into the void — let go of what’s finished'],
};

// Mirror order-insensitive pairs that aren't explicitly listed reversed.
const MIRROR_EXEMPT = new Set(Object.keys(PAIR_READINGS));
for (const key of [...MIRROR_EXEMPT]) {
  const rev = key[1] + key[0];
  if (!PAIR_READINGS[rev]) PAIR_READINGS[rev] = PAIR_READINGS[key];
}

const PATH_READINGS = {
  1: 'A leader’s number. This phone belongs to someone who starts things — calls get answered, plans get moving. Guard against going it alone when help is one call away.',
  2: 'A diplomat’s number. Conversations on this line soothe, persuade, and connect. People remember how you made them feel — this number makes sure they call back.',
  3: 'A warrior’s number. Quick decisions and fast dialing. Great for closing deals; count to three before sending the message you typed angry.',
  4: 'A messenger’s number. Words are this number’s currency — sales, teaching, storytelling. Whoever holds it rarely loses an argument, or a customer.',
  5: 'A sage’s number. Calls made on this line carry weight; advice given is taken. A strong number for teachers, advisors, and anyone people trust.',
  6: 'An artist’s number. Charm flows through this line — luck in love, taste, and money that arrives pleasantly. Enjoy it; just keep receipts.',
  7: 'A mountain’s number. Nothing shakes it. Progress is slower but permanent — what this number builds, it keeps. Patience is its superpower.',
  8: 'A dragon’s number. Intense, magnetic, a little unpredictable — big wins favor the bold. Keep a steady friend on speed dial for balance.',
  9: 'A fortunate number. The strongest finish in numerology — protection, progress, and doors that open at the right moment. Whoever answers this phone tends to land on their feet.',
};

// A curated set of short, encouraging Bible verses mapped by chapter:verse
// derived from the phone number's digits. We pick a book from the digit sum,
// then build chapter:verse from the last digits — always landing on a real,
// uplifting verse.
const BIBLE_BOOKS = [
  null, // 0 unused
  'Proverbs',   // 1
  'Psalms',     // 2
  'Isaiah',     // 3
  'Romans',     // 4
  'Matthew',    // 5
  'Philippians',// 6
  'Jeremiah',   // 7
  'Hebrews',    // 8
  'Ecclesiastes',// 9
];

const BIBLE_VERSES = {
  'Proverbs 3:5': 'Trust in the Lord with all your heart and lean not on your own understanding.',
  'Proverbs 3:6': 'In all your ways submit to him, and he will make your paths straight.',
  'Proverbs 4:7': 'The beginning of wisdom is this: Get wisdom. Though it cost all you have, get understanding.',
  'Proverbs 11:25': 'A generous person will prosper; whoever refreshes others will be refreshed.',
  'Proverbs 16:3': 'Commit to the Lord whatever you do, and he will establish your plans.',
  'Proverbs 16:9': 'In their hearts humans plan their course, but the Lord establishes their steps.',
  'Proverbs 22:1': 'A good name is more desirable than great riches; to be esteemed is better than silver or gold.',
  'Proverbs 27:17': 'As iron sharpens iron, so one person sharpens another.',
  'Psalms 23:1': 'The Lord is my shepherd, I lack nothing.',
  'Psalms 27:1': 'The Lord is my light and my salvation — whom shall I fear?',
  'Psalms 37:4': 'Take delight in the Lord, and he will give you the desires of your heart.',
  'Psalms 46:1': 'God is our refuge and strength, an ever-present help in trouble.',
  'Psalms 91:1': 'Whoever dwells in the shelter of the Most High will rest in the shadow of the Almighty.',
  'Psalms 118:24': 'This is the day the Lord has made; let us rejoice and be glad in it.',
  'Psalms 121:1': 'I lift up my eyes to the mountains — where does my help come from?',
  'Psalms 139:14': 'I praise you because I am fearfully and wonderfully made.',
  'Isaiah 40:31': 'Those who hope in the Lord will renew their strength. They will soar on wings like eagles.',
  'Isaiah 41:10': 'So do not fear, for I am with you; do not be dismayed, for I am your God.',
  'Isaiah 43:2': 'When you pass through the waters, I will be with you.',
  'Isaiah 54:17': 'No weapon forged against you will prevail.',
  'Isaiah 55:8': 'For my thoughts are not your thoughts, neither are your ways my ways.',
  'Romans 8:28': 'And we know that in all things God works for the good of those who love him.',
  'Romans 8:31': 'If God is for us, who can be against us?',
  'Romans 12:2': 'Do not conform to the pattern of this world, but be transformed by the renewing of your mind.',
  'Romans 15:13': 'May the God of hope fill you with all joy and peace as you trust in him.',
  'Matthew 5:14': 'You are the light of the world. A town built on a hill cannot be hidden.',
  'Matthew 6:33': 'But seek first his kingdom and his righteousness, and all these things will be given to you.',
  'Matthew 7:7': 'Ask and it will be given to you; seek and you will find; knock and the door will be opened.',
  'Matthew 11:28': 'Come to me, all you who are weary and burdened, and I will give you rest.',
  'Matthew 17:20': 'If you have faith as small as a mustard seed, you can say to this mountain, "Move," and it will move.',
  'Philippians 1:6': 'He who began a good work in you will carry it on to completion.',
  'Philippians 4:6': 'Do not be anxious about anything, but in every situation, by prayer and petition, present your requests to God.',
  'Philippians 4:13': 'I can do all this through him who gives me strength.',
  'Jeremiah 29:11': 'For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you.',
  'Jeremiah 33:3': 'Call to me and I will answer you and tell you great and unsearchable things.',
  'Hebrews 11:1': 'Now faith is confidence in what we hope for and assurance about what we do not see.',
  'Hebrews 13:5': 'Never will I leave you; never will I forsake you.',
  'Hebrews 13:8': 'Jesus Christ is the same yesterday and today and forever.',
  'Ecclesiastes 3:1': 'There is a time for everything, and a season for every activity under the heavens.',
  'Ecclesiastes 3:11': 'He has made everything beautiful in its time.',
  'Ecclesiastes 7:8': 'The end of a matter is better than its beginning, and patience is better than pride.',
  'Ecclesiastes 9:10': 'Whatever your hand finds to do, do it with all your might.',
};

function pickBibleVerse(digits, path) {
  const book = BIBLE_BOOKS[path];
  // Get all verses for this book
  const bookVerses = Object.entries(BIBLE_VERSES).filter(([ref]) => ref.startsWith(book));
  if (!bookVerses.length) return null;
  // Use last few digits to deterministically pick a verse
  const seed = digits.slice(-4).reduce((a, d, i) => a + d * (i + 1), 0);
  const [reference, text] = bookVerses[seed % bookVerses.length];
  return { reference, text };
}

function toDigits(phone) {
  return String(phone || '').replace(/\D/g, '').split('').map(Number);
}

function reduceToPath(n) {
  while (n > 9) n = String(n).split('').reduce((a, c) => a + Number(c), 0);
  return n || 9;
}

function pairReading(a, b) {
  const key = `${a}${b}`;
  const hit = PAIR_READINGS[key];
  if (hit) return { pair: key, score: hit[0], meaning: hit[1] };
  // Fallback: blend the two digits' identities.
  const da = DIGITS[a];
  const db = DIGITS[b];
  const kw = a === b ? da.keywords[0] : `${da.trait} meeting ${db.trait}`;
  return { pair: key, score: 5, meaning: `${capitalize(da.planet)} beside ${db.planet} — ${kw}` };
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function tierFor(score) {
  if (score >= 82) return { tier: 'Exceptionally auspicious', stars: 5 };
  if (score >= 68) return { tier: 'Auspicious', stars: 4 };
  if (score >= 52) return { tier: 'Balanced', stars: 3 };
  if (score >= 38) return { tier: 'Challenging', stars: 2 };
  return { tier: 'A number that builds character', stars: 1 };
}

// Main entry: full reading for a phone number.
function luckyReading(phone) {
  const d = toDigits(phone);
  if (d.length < 4) return null;

  // Adjacent pairs, weighted toward the tail — the digits people actually
  // recite when giving out their number.
  const pairs = [];
  let weighted = 0;
  let weightSum = 0;
  for (let i = 0; i + 1 < d.length; i++) {
    const p = pairReading(d[i], d[i + 1]);
    const weight = i >= d.length - 3 ? 2 : 1; // final two pairs count double
    pairs.push({ ...p, emphasized: weight > 1 });
    weighted += p.score * weight;
    weightSum += weight;
  }
  const score = Math.round((weighted / weightSum) * 10); // 0–100
  const { tier, stars } = tierFor(score);

  const sum = d.reduce((a, b) => a + b, 0);
  const path = reduceToPath(sum);
  const ruling = DIGITS[path];
  const dayColor = PLANET_DAY[path];

  const best = pairs.reduce((m, p) => (p.score > m.score ? p : m), pairs[0]);
  const worst = pairs.reduce((m, p) => (p.score < m.score ? p : m), pairs[0]);

  const lastDigit = d[d.length - 1];
  const tail = DIGITS[lastDigit];
  const verse = pickBibleVerse(d, path);

  return {
    score,
    tier,
    stars,
    sum,
    path,
    pathReading: PATH_READINGS[path],
    ruling: {
      planet: ruling.planet,
      trait: ruling.trait,
      day: dayColor.day,
      color: dayColor.color,
    },
    tail: {
      digit: lastDigit,
      planet: tail.planet,
      note: `Your number ends in ${lastDigit} — ${tail.planet} has the last word: ${tail.keywords.join(', ')}.`,
    },
    pairs,
    best,
    worst,
    verse,
  };
}

module.exports = luckyReading;
